const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const ADMIN_TOKEN_KEY = 'einv_admin_token'
const CACHE_TTL_MS = 15000

let deadlineByInvitationId = new Map()
let lastLoadedAt = 0
let loadingPromise = null
let renderQueued = false

function getApi(path) {
  return `${API_BASE_URL}${path}`
}

function getToken() {
  return localStorage.getItem(ADMIN_TOKEN_KEY) || ''
}

function normalizeInvitationId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase()
}

function normalizeDate(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  return match ? match[1] : ''
}

function formatDate(value) {
  const normalized = normalizeDate(value)
  if (!normalized) return '—'

  const date = new Date(`${normalized}T00:00:00`)
  if (Number.isNaN(date.getTime())) return normalized

  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

async function fetchJson(path, token) {
  try {
    const response = await fetch(getApi(path), {
      headers: { Authorization: `Bearer ${token}` }
    })

    if (!response.ok) return null
    return await response.json().catch(() => null)
  } catch {
    return null
  }
}

function getBookingInvitationId(booking) {
  const linkedId = normalizeInvitationId(
    booking?.linked_invitation_id || booking?.linkedInvitationId
  )
  if (linkedId) return linkedId

  return normalizeInvitationId(
    `${booking?.celebrant_name || booking?.full_name || ''}-${booking?.event_type || ''}`
  )
}

async function refreshDeadlineMap(force = false) {
  const token = getToken()
  if (!token) return deadlineByInvitationId

  if (!force && Date.now() - lastLoadedAt < CACHE_TTL_MS) {
    return deadlineByInvitationId
  }

  if (loadingPromise) return loadingPromise

  loadingPromise = (async () => {
    const [invitationJson, activeBookingJson, archivedBookingJson] = await Promise.all([
      fetchJson('/admin/invitations', token),
      fetchJson('/bookings?view=active', token),
      fetchJson('/bookings?view=archived', token)
    ])

    const nextMap = new Map()
    const invitationRows = Array.isArray(invitationJson?.rows) ? invitationJson.rows : []

    invitationRows.forEach((row) => {
      const id = normalizeInvitationId(row?.invitationId)
      const deadline = normalizeDate(row?.rsvpDeadline || row?.rsvp_deadline)
      if (id && deadline) nextMap.set(id, deadline)
    })

    const bookings = [
      ...(Array.isArray(activeBookingJson?.bookings) ? activeBookingJson.bookings : []),
      ...(Array.isArray(archivedBookingJson?.bookings) ? archivedBookingJson.bookings : [])
    ]

    bookings.forEach((booking) => {
      const invitationId = getBookingInvitationId(booking)
      const deadline = normalizeDate(booking?.rsvp_deadline || booking?.rsvpDeadline)
      if (invitationId && deadline && !nextMap.has(invitationId)) {
        nextMap.set(invitationId, deadline)
      }
    })

    deadlineByInvitationId = nextMap
    lastLoadedAt = Date.now()
    return deadlineByInvitationId
  })().finally(() => {
    loadingPromise = null
  })

  return loadingPromise
}

function findRsvpStatusHeader(headerRow) {
  return Array.from(headerRow?.children || []).find((cell) => (
    !cell.dataset.adminRsvpDeadlineHeader &&
    String(cell.textContent || '').trim().toLowerCase() === 'rsvp'
  )) || null
}

function ensureDeadlineHeader(headerRow) {
  let deadlineHeader = headerRow.querySelector('[data-admin-rsvp-deadline-header="true"]')
  if (deadlineHeader) return deadlineHeader

  const rsvpStatusHeader = findRsvpStatusHeader(headerRow)
  if (!rsvpStatusHeader) return null

  deadlineHeader = document.createElement('th')
  deadlineHeader.dataset.adminRsvpDeadlineHeader = 'true'
  deadlineHeader.textContent = 'RSVP Deadline'
  deadlineHeader.style.whiteSpace = 'nowrap'
  headerRow.insertBefore(deadlineHeader, rsvpStatusHeader)
  return deadlineHeader
}

function renderDeadlineColumn() {
  if (!location.pathname.startsWith('/admin/invitations')) return

  const table = document.querySelector('.adm-inv-section table.adm-table')
  const headerRow = table?.querySelector('thead tr')
  if (!table || !headerRow) return

  const deadlineHeader = ensureDeadlineHeader(headerRow)
  if (!deadlineHeader) return

  const deadlineIndex = Array.from(headerRow.children).indexOf(deadlineHeader)
  if (deadlineIndex < 0) return

  table.querySelectorAll('tbody tr').forEach((row) => {
    const emptyCell = row.querySelector('.adm-table-empty')
    if (emptyCell) {
      emptyCell.colSpan = headerRow.children.length
      return
    }

    const idCell = row.querySelector('.adm-table-id')
    if (!idCell) return

    let deadlineCell = row.querySelector('[data-admin-rsvp-deadline-cell="true"]')
    if (!deadlineCell) {
      deadlineCell = document.createElement('td')
      deadlineCell.dataset.adminRsvpDeadlineCell = 'true'
      deadlineCell.style.fontSize = '12px'
      deadlineCell.style.whiteSpace = 'nowrap'
      deadlineCell.style.color = 'var(--adm-text-muted)'

      const referenceCell = row.children[deadlineIndex] || null
      row.insertBefore(deadlineCell, referenceCell)
    }

    const invitationId = normalizeInvitationId(idCell.textContent)
    const deadline = deadlineByInvitationId.get(invitationId) || ''
    const nextText = formatDate(deadline)

    if (deadlineCell.textContent !== nextText) {
      deadlineCell.textContent = nextText
      deadlineCell.title = deadline
        ? `RSVP deadline: ${formatDate(deadline)}`
        : 'No RSVP deadline is saved for this invitation.'
    }
  })
}

function queueRender() {
  if (renderQueued) return
  renderQueued = true

  requestAnimationFrame(() => {
    renderQueued = false
    renderDeadlineColumn()
  })
}

function startEnhancer() {
  if (window.__adminRsvpDeadlineColumnEnhancerStarted) return
  window.__adminRsvpDeadlineColumnEnhancerStarted = true

  const refreshAndRender = async (force = false) => {
    await refreshDeadlineMap(force)
    renderDeadlineColumn()
  }

  queueRender()
  refreshAndRender()

  const observer = new MutationObserver(queueRender)
  observer.observe(document.body, { childList: true, subtree: true })

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button')
    if (!button) return

    const label = String(button.textContent || '').toLowerCase()
    if (label.includes('refresh') || label.includes('sync designs')) {
      window.setTimeout(() => refreshAndRender(true), 700)
    }
  })

  window.setInterval(() => refreshAndRender(true), CACHE_TTL_MS)
}

if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startEnhancer, { once: true })
  } else {
    startEnhancer()
  }
}
